import { dao } from '@platform/dao';
import type { IRecord, IRecordListParams } from '@platform/core/types';
import { baseApi, listTag, run } from '@platform/state/api/baseApi';
import type { ILinkCandidatesArg } from './ILinkCandidatesArg';
import type { ILinkCandidatesPage } from './ILinkCandidatesPage';

/** Найбільша сторінка списку API. */
const PAGE = 100;
/** Скільки рядків показує вікно (прототип lkAdv: перші 200). */
const LIMIT = 200;

/**
 * Кандидати зв'язку одним запитом (прототип lkAdv kind record): перші сторінки вибраних типів паралельно, за номером, з пошуком `q`
 * (назва або код, як фільтр прототипу); типу, який сам має дати більше сотні з перших 200 рядків, дочитується друга сторінка; тип, який не
 * вдалося прочитати, дає порожню сторінку. Кешується зі списковими тегами типів.
 */
export const linkCandidatesApi = baseApi.injectEndpoints({
  endpoints: (b) => ({
    linkCandidates: b.query<ILinkCandidatesPage[], ILinkCandidatesArg>({
      queryFn: ({ ws, types, q }) => run(async () => {
        const params = (page: number): IRecordListParams => ({ page, pageSize: PAGE, sort: 'code', facets: false, ...(q ? { q } : {}) });
        const settled = await Promise.allSettled(types.map((rt) => dao.records.list(ws, rt, params(1))));
        const pages: ILinkCandidatesPage[] = settled.map((r, i) => ({
          rt: types[i]!, total: r.status === 'fulfilled' ? r.value.total : 0, items: r.status === 'fulfilled' ? r.value.items : [],
        }));
        let shown = 0;
        for (const p of pages) {
          if (shown < LIMIT && p.items.length === PAGE && p.total > PAGE && shown + PAGE < LIMIT) {
            const more: IRecord[] = await dao.records.list(ws, p.rt, params(2)).then((r) => r.items, () => []);
            p.items = [...p.items, ...more];
          }
          shown += p.items.length;
        }
        return pages;
      }),
      providesTags: (_r, _e, { types }) => types.map((rt) => listTag(rt)),
    }),
  }),
});

export const { useLinkCandidatesQuery } = linkCandidatesApi;
